import React, { useCallback, useEffect, useState } from 'react'
import {
  Avatar,
  Card,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { FlexHorizontal, Spacer } from '@vestaboard/installables'
import { useParams } from 'react-router'
import { DroppableTableBody } from '../../ui/DropableTableBody'
import DragIndicatorIcon from '@material-ui/icons/DragIndicator'
import { TruncatedId } from '../truncated-id'
import { DraggableTableRow } from '../../ui/DraggableTableRow'
import { useUpdateMarketplaceFeatureTypeV2 } from '../../api/useUpdateMarketplaceFeatureTypeV2'
import { useGetMarketplaceFeatureTypeV2Listings } from '../../api/useGetMarketplaceFeatureTypeV2Listings'

export const MarketplaceListingFeatureTypeList = () => {
  const { id } = useParams<{ id: string }>()
  const { data, loading, error } = useGetMarketplaceFeatureTypeV2Listings(id)
  const [updateV2] = useUpdateMarketplaceFeatureTypeV2()
  const [sortOrder, setSortOrder] = useState<string[]>([])

  const featureType =
    data?.marketplaceFeatureTypeV2?.__typename === 'MarketplaceFeatureTypeV2' ? data.marketplaceFeatureTypeV2 : null
  const listings = (featureType?.marketplaceListings || []).filter((listing): listing is NonNullable<typeof listing> =>
    Boolean(listing)
  )

  useEffect(() => {
    setSortOrder(listings.map(listing => listing.id))
  }, [featureType?.id, listings.map(listing => listing.id).join(',')])

  const items = sortOrder
    .map(listingId => listings.find(listing => listing.id === listingId))
    .filter((listing): listing is NonNullable<typeof listing> => Boolean(listing))

  const onDragEnd = useCallback(
    async (result: import('react-beautiful-dnd').DropResult) => {
      if (!result.destination || !featureType) {
        return
      }

      const newItems = Array.from(sortOrder)
      const [removed] = newItems.splice(result.source.index, 1)
      newItems.splice(result.destination.index, 0, removed)

      setSortOrder(newItems)

      await updateV2({
        variables: {
          input: {
            id,
            title: featureType.title || '',
            marketplaceListingIds: newItems
          }
        }
      })
    },
    [sortOrder, id, updateV2, featureType]
  )

  if (loading) {
    return <CircularProgress />
  }

  if (error || !featureType) {
    return <Alert severity='error'>There was an error loading this marketplace listing type</Alert>
  }

  return (
    <>
      <FlexHorizontal spaceBetween>
        <Typography variant='h4'>Marketplace Listing Type / {featureType.title}</Typography>
      </FlexHorizontal>
      <Spacer size='extraLarge' />
      {!items.length ? (
        <Alert severity='warning'>There are no marketplace listings associated with this type</Alert>
      ) : (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 50 }}></TableCell>
                  <TableCell style={{ width: 50 }}></TableCell>
                  <TableCell style={{ width: 170 }}>Listing ID</TableCell>
                  <TableCell>Listing Title</TableCell>
                </TableRow>
              </TableHead>
              <TableBody component={DroppableTableBody(onDragEnd)}>
                {items.map((item, index) => (
                  <TableRow key={item.id} component={DraggableTableRow(item.id, index)}>
                    <TableCell>
                      <DragIndicatorIcon />
                    </TableCell>
                    <TableCell>
                      {item.icon ? <Avatar src={item.icon} /> : <Avatar>{item.title?.split('')[0]}</Avatar>}
                    </TableCell>
                    <TableCell>
                      <TruncatedId value={item.id} full path={`/marketplace-listings/${item.id}`} />
                    </TableCell>
                    <TableCell>{item.title}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </>
  )
}
